import { useNavigate } from '@remix-run/react';
import { useRef, useState } from 'react';
import type { CollectionsQuery } from '~/generated/graphql';

interface CategoriesSectionProps {
    collections: CollectionsQuery['collections']['items'];
}

export function CategoriesSection({ collections }: CategoriesSectionProps) {
    const navigate = useNavigate();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    // Flatten all children from collections to show sub-categories only
    const subCategories = collections.flatMap(c => c.children || []);

    if (!subCategories || subCategories.length === 0) {
        return null;
    }

    const [moved, setMoved] = useState(false);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!scrollContainerRef.current) return;
        setIsDragging(true);
        setMoved(false);
        setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
        setScrollLeft(scrollContainerRef.current.scrollLeft);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !scrollContainerRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        if (Math.abs(x - startX) > 5) {
            setMoved(true);
        }
        scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleCategoryClick = (slug: string) => {
        if (!moved) {
            navigate(`/collections/${slug}`);
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    return (
        <section
            className="mb-4 flex flex-col"
            style={{
                width: '100%',
                borderRadius: '24px',
                border: '1px solid #F2F2F2',
                padding: '24px',
                gap: '16px',
                backgroundColor: '#FFFFFF',
            }}
        >
            {/* Heading */}
            <h2
                className="font-semibold mb-2 sm:mb-3"
                style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                    fontSize: '22px',
                    lineHeight: '28px',
                    letterSpacing: '-0.02em',
                    color: '#1A090E',
                }}
            >
                All Categories
            </h2>

            {/* Categories Horizontal Scroll with Drag */}
            <div
                ref={scrollContainerRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                className="overflow-x-auto -mx-1 [&::-webkit-scrollbar]:hidden cursor-grab active:cursor-grabbing"
                style={{
                    height: '140px',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
            >
                <div className="flex gap-3 sm:gap-4 pb-2 px-1 h-full items-start">
                    {subCategories.map((collection) => (
                        <button
                            key={collection.id}
                            onClick={() => handleCategoryClick(collection.slug)}
                            className="flex flex-col items-center group flex-shrink-0"
                        >
                            {/* Rounded Square Icon Container */}
                            <div className="w-14 h-14 sm:w-18 sm:h-18 md:w-20 md:h-20 rounded-[14px] sm:rounded-[18px] bg-[#F5F5F5] shadow-sm flex items-center justify-center mb-1.5 sm:mb-2 transition-transform hover:scale-105 overflow-hidden p-0">
                                {collection.featuredAsset?.preview ? (
                                    <img
                                        src={`${collection.featuredAsset.preview}?w=120&h=120&format=webp`}
                                        alt={collection.name}
                                        className="w-full h-full object-contain"
                                    />
                                ) : (
                                    <img
                                        src="/favicon.ico"
                                        alt={collection.name}
                                        className="w-full h-full object-contain opacity-50"
                                    />
                                )}
                            </div>

                            {/* Category Name */}
                            <span className="text-[10px] sm:text-xs font-medium text-gray-900 text-center line-clamp-2 w-16 sm:w-20 leading-tight">
                                {collection.name}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
}
